// Types
type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
type StatusCode = 200 | 400 | 500 | number

interface PendingRequest {
  method: HttpMethod
  url: string
  payload?: unknown
  timestamp: number
  groupTitle: string
  requestId: string
}

interface LogResponse {
  status?: StatusCode
  data?: unknown
}

// Constants
// Request payloads go to the console verbatim, and LogRocket records the
// console, so this is a local-dev tool only: every deployed build, previews
// included, has NODE_ENV=production.
const isNetworkLoggingDisabled =
  process.env.NODE_ENV !== 'development' ||
  process.env.DISABLE_NETWORK_LOGGING === 'true'

// State
const pendingRequests = new Map<string, PendingRequest>()
let requestCounter = 0

// Helper functions
const getRequestKey = (
  method: HttpMethod,
  url: string,
  requestId?: string,
): string => requestId || `${method}-${url}-${++requestCounter}`

const findPendingRequest = (
  method: HttpMethod,
  url: string,
  timestamp: number,
): PendingRequest | undefined =>
  Array.from(pendingRequests.values()).find(
    (req) =>
      req.method === method && req.url === url && req.timestamp === timestamp,
  )

const logGroupInfo = (
  pendingRequest: PendingRequest,
  status: StatusCode,
  duration: number,
): void => {
  console.groupCollapsed(
    `${pendingRequest.groupTitle} ${status} (${duration}ms)`,
  )
}

const logPayload = (payload?: unknown): void => {
  if (!payload) return
  console.groupCollapsed('Request Payload:')
  console.info(payload)
  console.groupEnd()
}

const logResponse = (title: string, data: unknown): void => {
  console.groupCollapsed(title)
  console.info(data)
  console.groupEnd()
}

const handleResponse = (
  method: HttpMethod,
  url: string,
  response: LogResponse,
  startTimestamp: number,
  isError = false,
): void => {
  if (isNetworkLoggingDisabled) return

  const endTimestamp = Date.now()
  const duration = endTimestamp - startTimestamp
  const status = response?.status || (isError ? 500 : 200)

  const pendingRequest = findPendingRequest(method, url, startTimestamp)
  if (!pendingRequest) return

  logGroupInfo(pendingRequest, status, duration)
  logPayload(pendingRequest.payload)
  logResponse(isError ? 'Error:' : 'Response:', response)
  console.groupEnd()

  pendingRequests.delete(pendingRequest.requestId)
}

// Exported network logger
export const networkLogger = {
  request: (
    method: HttpMethod,
    url: string,
    payload?: unknown,
  ): number | undefined => {
    if (isNetworkLoggingDisabled) return

    const timestamp = Date.now()
    const requestId = getRequestKey(method, url)
    const groupTitle = `[MOMENT] ${method} ${url}`

    pendingRequests.set(requestId, {
      method,
      url,
      payload,
      timestamp,
      groupTitle,
      requestId,
    })

    return timestamp
  },

  error: (
    method: HttpMethod,
    url: string,
    error: unknown,
    startTimestamp: number,
  ): void => {
    handleResponse(method, url, error as LogResponse, startTimestamp, true)
  },
}
